import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { CALCULATORS } from "@/data/calculators";

// Instant client-side search + suggestions dropdown.
export default function SearchBar({ onPick, testIdPrefix = "search" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim().toLowerCase()), 120);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const matches = useMemo(() => {
    if (!debounced) return [];
    return CALCULATORS.filter((c) => {
      const hay = [c.name, c.category, c.description, ...(c.keywords || [])].join(" ").toLowerCase();
      return hay.includes(debounced);
    }).slice(0, 8);
  }, [debounced]);

  return (
    <div ref={ref} className="relative w-full">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          data-testid={`${testIdPrefix}-input`}
          type="search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search calculators — try 'bmi', 'macros', 'pregnancy'…"
          className="w-full h-11 rounded-full border border-border bg-background pl-10 pr-10 text-sm outline-none transition focus:ring-2 focus:ring-ring/40 focus:border-foreground/40 placeholder:text-muted-foreground"
          aria-label="Search calculators"
          autoComplete="off"
        />
        {q && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => { setQ(""); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && debounced && (
        <div
          data-testid={`${testIdPrefix}-suggestions`}
          className="absolute z-50 mt-2 w-full rounded-xl border border-border bg-popover shadow-lg overflow-hidden"
        >
          {matches.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">No calculators found for "{q}"</div>
          ) : (
            <ul className="max-h-80 overflow-auto py-1">
              {matches.map((m) => (
                <li key={m.slug}>
                  <button
                    type="button"
                    data-testid={`suggestion-${m.slug}`}
                    onClick={() => { setQ(""); setOpen(false); onPick?.(m.slug); }}
                    className="w-full text-left px-4 py-2.5 hover:bg-accent flex flex-col gap-0.5"
                  >
                    <span className="text-sm font-medium">{m.name}</span>
                    <span className="text-xs text-muted-foreground">{m.description}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
